import React, {useEffect, useRef} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import Content from "../Layout/Content";
import {Alert, Spin, Input, Button, Grid, Breadcrumb, Message, Modal, Switch} from "@arco-design/web-react";
import {useAuth0} from '@auth0/auth0-react';
import {FetchApi, PostApi, OverwriteApi, DeleteApi} from "../Api/AuthenticatedApi";
import DangerZone from "./Editors/DangerZone";
import { emailDeleteNotes } from "./Editors/deleteNotes";
import SummaryCard from "../Layout/SummaryCard";
import {IconSave as SaveIcon, IconArrowLeft} from "@arco-design/web-react/icon";
import {confirmIfLive} from "../utils/confirmIfLive";
import EmailComposer from "../Editor/EmailComposer/EmailComposer";
import { deadLinks, hasConfirmLink } from "../Editor/EasyEmail/campaignTags";
import RecordNotFound from "../Components/RecordNotFound";

const {Row, Col} = Grid;

// MUI -> Arco + styled-components. makeStyles classes become styled-components.
// The classNames they expose (.styledComponentId) are passed through the same
// prop NAMES (className / classNameToolBar / classNameInner) into the local
// EmailViewer / EmailSourceEditor helpers, which still apply them as plain
// className strings on their <div>s.

export default function MailVariantEditor() {
    const {campaignId, mailVariantId} = useParams();
    // Which email slot it's a design for: the signup email, or the follow-up
    // (?role=followup when making one; else the saved email's).
    const [searchParams] = useSearchParams();
    const [role, setRole] = React.useState(searchParams.get("role") === "followup" ? "followup" : "signup");
    const isFollowup = role === "followup";
    // const {campaignId} = useParams();
    const [record, setRecord] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [loadFailed, setLoadFailed] = React.useState(false);
    // Saving flips the composer to read-only + reseeds it, which remounts the
    // heavy Easy Email editor. Cover that visible gap with a spinner overlay.
    const [saving, setSaving] = React.useState(false);
    // const [value, setValue] = React.useState("0")

    // ui controls

    // controlled variables for the record
    const [shouldRun, setShouldRun] = React.useState(false)
    const [variantName, setVariantName] = React.useState("");
    const [campaignName, setCampaignName] = React.useState("");
    // Double opt-in: this email must carry the confirm link (they aren't
    // subscribed until they click it), so the editor offers a Confirm button
    // and saving warns if it's missing.
    const [doubleOptIn, setDoubleOptIn] = React.useState(false);
    const [campaignEmails, setCampaignEmails] = React.useState([]); // for the delete confirmation
    const [deleting, setDeleting] = React.useState(false);
    // Clicking the confirm (or unsubscribe / tracked) link in the preview
    // points to Test drive, where the whole flow can be tried. A new tab keeps
    // unsaved edits here.
    const [testDriveHint, setTestDriveHint] = React.useState(false);
    const onFlowLink = React.useCallback(() => setTestDriveHint(true), []);

    // The email content is owned by the shared <EmailComposer>; read at save time
    // via composerRef.getContent(). composerSeed + seedKey (re)seed on load/cancel.
    const composerRef = useRef(null);
    const [composerSeed, setComposerSeed] = React.useState({});
    const [seedKey, setSeedKey] = React.useState(0);

    const {getAccessTokenSilently} = useAuth0();
    const history = useNavigate();

    const [templateModalVisible, setTemplateModalVisible] = React.useState(false);
    const [templateName, setTemplateName] = React.useState("");
    const [allTemplates, setAllTemplates] = React.useState([]);

    // The shared composer owns the content; pull the compiled html + design out
    // of it at save time.
    const currentContent = () => (composerRef.current ? composerRef.current.getContent() : {});

    const handleSaveAsTemplate = () => {
        const name = templateName.trim();
        if (!name) return;
        PostApi(getAccessTokenSilently, "/api/email-templates/",
            () => { setTemplateModalVisible(false); setTemplateName(""); Message.success("Saved as template"); },
            () => history("/login"),
            () => Message.error("Failed to save template"),
            { name, ...currentContent() });
    };

    React.useEffect(() => {
        if (mailVariantId !== "new") return;
        FetchApi(getAccessTokenSilently, "/api/email-templates/",
            (data) => setAllTemplates(data || []),
            () => history("/login"),
            () => {});
    }, [mailVariantId, getAccessTokenSilently, history]);

    const linkStyle = { cursor: "pointer", color: "rgb(var(--arcoblue-6))" };

    const save = async () => {
        // The Easy Email editor owns the content: pull the compiled HTML +
        // re-editable design out of it at save time.
        const content = currentContent();
        // A subject is required (without one, inboxes show "(no subject)").
        if (!(content.mail_subject_template || "").trim()) {
            if (composerRef.current) composerRef.current.flagSubject();
            Message.error("Add a subject line: it's required.");
            return;
        }
        const ok = await confirmIfLive({ live: isLive, title: "This email is live", content: liveReason });
        if (!ok) return;
        // Catch the two ways this email can leave people stuck: no confirm link
        // on a double opt-in campaign, or buttons that don't link anywhere.
        const problems = [];
        if (doubleOptIn && !isFollowup && !hasConfirmLink(content.mail_html_template, content.mail_text_template)) {
            problems.push("There's no confirm link. People aren't subscribed, and don't get the reward, until they click it: add the Confirm button (Campaign blocks in the editor's left panel).");
        }
        const dead = deadLinks(content.mail_html_template);
        if (dead.length) {
            problems.push(`${dead.length === 1 ? "This button doesn't" : "These buttons don't"} link anywhere yet: ${dead.map((d) => `“${d}”`).join(", ")}. Set a link on ${dead.length === 1 ? "it" : "them"}, or replace ${dead.length === 1 ? "it" : "them"} with the Confirm button.`);
        }
        if (problems.length) {
            const proceed = await new Promise((resolve) => Modal.confirm({
                title: "This email isn't ready to send",
                content: (
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {problems.map((p) => <li key={p} style={{ marginBottom: 6 }}>{p}</li>)}
                    </ul>
                ),
                okText: "Save anyway",
                cancelText: "Keep editing",
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            }));
            if (!proceed) return;
        }
        setSaving(true);
        saveVariant({
            name: variantName,
            should_run: shouldRun,
            role,
            ...content,
        });
    }

    const setFromRecord = (r) => {
        if (r.role) setRole(r.role);
        setVariantName("" + (r.name || ""))
        setShouldRun(r.should_run)
        // Hand the loaded content to the composer and bump seedKey so it reseeds
        // (also the reset path when the user cancels an edit).
        setComposerSeed({
            subject: r.mail_subject_template,
            html: r.mail_html_template,
            text: r.mail_text_template,
            design: r.editor_design,
        })
        setSeedKey((k) => k + 1)
    }

    useEffect(() => {
        if (mailVariantId === "new") {
            setLoading(false); // nothing to load — start from a blank email
            return;
        }
        (async () => {
            let url = "/api/campaigns/" + campaignId + "/email/variants/" + mailVariantId
            FetchApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                    setFromRecord(data);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    setLoadFailed(true);
                })
        })();
    }, [campaignId, mailVariantId, setRecord, setLoading, history, getAccessTokenSilently])

    // Fetch the campaign name for the breadcrumb (the variant record doesn't
    // carry it).
    useEffect(() => {
        if (!campaignId) return;
        FetchApi(getAccessTokenSilently, "/api/campaigns/" + campaignId,
            (data) => {
                setCampaignName((data && data.name) || "");
                setDoubleOptIn(!!(data && data.double_opt_in));
                setCampaignEmails((data && data.mail_variants) || []);
            },
            () => {}, () => {});

    }, [campaignId, getAccessTokenSilently])

    // Delete (archive) this email, then back to the campaign's signup emails.
    const deleteEmail = () => new Promise((resolve, reject) => {
        setDeleting(true);
        DeleteApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/email/variants/${mailVariantId}`,
            () => {
                setDeleting(false);
                Message.success("Signup email deleted");
                history(`/signups/${campaignId}/edit?section=confirmation`);
                resolve();
            },
            () => { setDeleting(false); reject(); },
            (err) => { setDeleting(false); Message.error((err && err.message) || "Couldn't delete the email"); reject(err); });
    });

    if (loadFailed) {
        return <RecordNotFound what="email" backTo={`/signups/${campaignId}/edit`} backLabel="Back to the signup" />;
    }
    if (loading) {
        return (
            <Content>
                <Spin/>
            </Content>
        );
    }

    function saveVariant(data) {
        if (mailVariantId === "new") {
            createVariant(data);
            return;
        }
        (async () => {
            // Backend registers this path as PUT (HandleModifyMailVariant).
            let url = "/api/campaigns/" + campaignId + "/email/variants/" + mailVariantId;
            OverwriteApi(getAccessTokenSilently, url,
                (saved) => {
                    setLoading(false);
                    setRecord(saved);
                    setFromRecord(saved); // reload editor with the saved design
                    setSaving(false);
                    Message.success("Email variant saved");
                },
                (response) => {
                    setLoading(false);
                    setSaving(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    setSaving(false);
                    Message.error("Failed to save email variant");
                }, data);
        })();
    }

    // For a brand-new variant: POST to create, then route to the real id so
    // subsequent saves edit it (mirrors the broadcast email editor).
    function createVariant(data) {
        (async () => {
            let url = "/api/campaigns/" + campaignId + "/email/variants/";
            PostApi(getAccessTokenSilently, url,
                (created) => {
                    Message.success("Email variant created");
                    history("/signups/" + campaignId + "/edit/mail/" + created.id);
                },
                (response) => {
                    setLoading(false);
                    setSaving(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    setSaving(false);
                    Message.error("Failed to create email variant");
                }, data);
        })();
    }

    const sent = (record && record.sent) || 0;
    const opens = (record && record.opens) || 0;
    const isLive = !!(record && record.should_run) && (sent > 0 || opens > 0);
    const liveReason = `This email is active and has been sent ${sent.toLocaleString()} time${sent === 1 ? "" : "s"}. Saving changes the email that goes out.`;

    return (
        <Content>
            {/* <div
                style={{
                    height: "200px",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                    filter: "contrast(75%)",
                    backgroundImage: "url(/img/wallpaper.jpeg)",
                }}
            /> */}

            <div>

                <Breadcrumb separator="›">
                    <Breadcrumb.Item style={linkStyle} onClick={() => history("/")}>
                        Home
                    </Breadcrumb.Item>
                    <Breadcrumb.Item style={linkStyle} onClick={() => history("/signups")}>
                        Signups
                    </Breadcrumb.Item>
                    <Breadcrumb.Item style={linkStyle} onClick={() => history(`/signups/${campaignId}/edit?section=confirmation`)}>
                        {campaignName || "Campaign"}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Edit Email</Breadcrumb.Item>
                </Breadcrumb>

            </div>

            <div style={{ margin: "12px 0" }}>
                <Button
                    icon={<IconArrowLeft />}
                    onClick={() => history(`/signups/${campaignId}/edit?section=confirmation`)}
                >
                    Back to {campaignName || "campaign"}
                </Button>
            </div>

            <div>

                {/*<SummaryCardGroup>*/}
                {/*    <SummaryCard title={"Opens"} value={record.opens}/>*/}
                {/*    <SummaryCard title={"Unsubscribes"} value={record.unsubscribes}/>*/}
                {/*    <SummaryCard title={"Conversions"} value={record.conversions}/>*/}
                {/*</SummaryCardGroup>*/}

                {/*<SummaryCard title={"Variant Settings"}>*/}
                {/*    <>*/}
                {/*        <TextField*/}
                {/*            autoFocus*/}
                {/*            margin="dense"*/}
                {/*            id="name"*/}
                {/*            label="Name"*/}
                {/*            fullWidth*/}

                {/*            disabled={!formSavable}*/}
                {/*            className={classes.wideInputs}*/}
                {/*            value={variantName}*/}
                {/*            onChange={(e) => {*/}
                {/*                setVariantName(e.target.value);*/}
                {/*            }}*/}
                {/*        />*/}
                {/*        <TextField*/}
                {/*            autoFocus*/}
                {/*            margin="dense"*/}
                {/*            id="name"*/}
                {/*            label="Topic"*/}
                {/*            fullWidth*/}

                {/*            disabled={!formSavable}*/}
                {/*            className={classes.wideInputs}*/}
                {/*            value={variantTopic}*/}
                {/*            onChange={(e) => {*/}
                {/*                setVariantTopic(e.target.value);*/}
                {/*            }}*/}
                {/*        />*/}

                {/*        <FormGroup>*/}
                {/*            <FormControlLabel*/}
                {/*                disabled={!formSavable}*/}
                {/*                checked={shouldRun}*/}
                {/*                onChange={(e) => {*/}
                {/*                    console.log("HEY: ", e.target.checked);*/}
                {/*                    setShouldRun(e.target.checked)*/}
                {/*                }}*/}
                {/*                control={<Checkbox/>}*/}
                {/*                label="Enabled"*/}
                {/*                size={"large"}*/}
                {/*            />*/}
                {/*        </FormGroup>*/}

                {/*        <ButtonGroup color="primary" aria-label="outlined primary button group">*/}
                {/*            <Button color="primary" onClick={editOrCancel}>{editOrCancelText}</Button>*/}
                {/*            <Button color="secondary" onClick={saveChanges} disabled={!formSavable}>Save</Button>*/}
                {/*        </ButtonGroup>*/}
                {/*    </>*/}
                {/*</SummaryCard>*/}


                <SummaryCard title={"Edit Email"}>
                    <Row gutter={16}>
                        <Col span={24} style={{ marginBottom: 12 }}>
                            <Button.Group aria-label="outlined primary button group">
                                <Button type="primary" onClick={save} icon={<SaveIcon/>}>Save</Button>
                                <Button onClick={() => setFromRecord(record)}>Discard</Button>
                                <Button onClick={() => setTemplateModalVisible(true)}>Save as template</Button>
                            </Button.Group>
                        </Col>
                        <Col span={24} style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 20 }}>
                                <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                                    <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Email name</div>
                                    <Input
                                        value={variantName}
                                        placeholder="e.g. Version A"
                                        onChange={setVariantName}
                                    />
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Status</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, height: 32 }}>
                                        <Switch checked={shouldRun} onChange={setShouldRun} />
                                        <span style={{ color: "var(--color-text-2)" }}>{shouldRun ? "Active" : "Paused"}</span>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col span={24}>
                            <Spin loading={saving} tip="Saving…" style={{ display: "block", width: "100%" }}>
                                {isFollowup && (
                                    <Alert
                                        type="info"
                                        style={{ marginBottom: 12 }}
                                        content="This is a follow-up email design: it goes once they confirm, and brings their reward. The reward's button is added below your design automatically."
                                    />
                                )}
                                {doubleOptIn && !isFollowup && (
                                    <Alert
                                        type="info"
                                        style={{ marginBottom: 12 }}
                                        content="This is the confirmation email: add the Confirm button (Campaign blocks in the editor's left panel). People aren't subscribed until they click it. To try the whole flow, use Test drive on the campaign page."
                                    />
                                )}
                                <EmailComposer
                                    ref={composerRef}
                                    initial={composerSeed}
                                    seedKey={seedKey}
                                    disabled={false}
                                    templates={mailVariantId === "new" ? allTemplates : undefined}
                                    enablePersonalize
                                    confirmLink={doubleOptIn && !isFollowup}
                                    onFlowLink={onFlowLink}
                                    requireSubject
                                />
                            </Spin>
                        </Col>
                    </Row>

                </SummaryCard>

                {mailVariantId && mailVariantId !== "new" && (
                    <DangerZone
                        description="Delete this signup email from the campaign. It won't be sent to new signups."
                        buttonLabel="Delete email"
                        confirmTitle={`Delete “${variantName || "this email"}”?`}
                        notes={emailDeleteNotes(mailVariantId, campaignEmails.filter((m) => !m.builtin && (m.role || "signup") === role), doubleOptIn && !isFollowup)}
                        onConfirm={deleteEmail}
                        loading={deleting}
                    />
                )}

            </div>

            <Modal
                title="Try it in Test drive"
                visible={testDriveHint}
                onCancel={() => setTestDriveHint(false)}
                onOk={() => {
                    setTestDriveHint(false);
                    window.open(`/signups/${campaignId}/edit?testdrive=1`, "_blank", "noopener");
                }}
                okText="Open Test drive"
                cancelText="Close"
            >
                This link goes live in the real email. To try the whole flow (your form, this email arriving, the confirm
                click and what happens next), use Test drive. It uses the campaign's saved settings, so save this email first
                if you've changed it. Nothing is saved during a test drive.
            </Modal>

            <Modal
                title="Save as template"
                visible={templateModalVisible}
                onOk={handleSaveAsTemplate}
                onCancel={() => setTemplateModalVisible(false)}
                okText="Save template"
                simple
            >
                <p style={{ color: "var(--color-text-3)", marginTop: 0 }}>
                    Saves this email's design as a reusable template.
                </p>
                <Input
                    placeholder="Template name (e.g. Monthly newsletter)"
                    value={templateName}
                    onChange={setTemplateName}
                    onPressEnter={handleSaveAsTemplate}
                />
            </Modal>
        </Content>
    );
}
